import { Box } from '@chakra-ui/react';
import React from 'react';

export interface TableContainerProps {
  children: React.ReactNode;
  e2eTitle?: string;
}

/**
 * Flex-based container for table pages.
 *
 * Provides the flex="1" minHeight="0" pattern for reliable height
 * calculation in flex parents, with border/background/overflow handling.
 *
 * @useFor the outermost wrapper of any page whose primary content is
 *   a `<DataTable>` filling vertical space. Solves the
 *   notorious-since-the-dawn-of-flexbox `flex="1" minHeight="0"`
 *   incantation so callers don't get it wrong.
 *
 *   Examples in-tree: People page, Roles page, Onboarding jobs, every
 *   list-shaped admin page in SMS / AIM.
 *
 *   For card-grid pages (`<SectionCard>` repeated, no table) use
 *   plain Chakra `<Box>` / `<Stack>`. For modal/drawer table content
 *   use `<DrawerComponent>` directly — it owns its own scroll model.
 */
export function TableContainer({ children, e2eTitle }: TableContainerProps) {
  return (
    <Box
      flex="1"
      minHeight="0"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      border="1"
      background="surface.primary"
      borderColor="border.secondary"
      borderRadius="xl"
      minWidth="0"
      maxWidth="100%"
    >
      <Box
        data-e2e={e2eTitle}
        flex="1"
        minHeight="0"
        display="flex"
        flexDirection="column"
        minWidth="0"
        maxWidth="100%"
        overflow="hidden"
      >
        {children}
      </Box>
    </Box>
  );
}
